import { css } from "@emotion/react";
import { useMemo } from "react";
import * as React from "react";
import { connect, useSelector } from "react-redux";
import { RowInfo } from "react-table";

import { DesignToken, SpacingToken } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import { IDateRangeWithCompareFilter } from "constants/filters";
import { ReportDistribution } from "constants/reports/distribution";
import { ReportType } from "constants/reports/report-type";
import { useLastVisitedReport } from "hooks/reports/use-last-visited-report";
import { IStoreState } from "interfaces/storeState";
import { ITicketRatingsPerEntity } from "services/api/report/interfaces";
import { selectAgents } from "store/entities/agents";
import { selectFeatureTogglePerformanceReport } from "store/entities/license";
import { Plan, selectCurrentSubscriptionPlan } from "store/entities/subscriptions";
import { selectTeams } from "store/entities/teams";
import {
  ITicketRatingsData,
  getDistribution,
  getFilterDateRange,
  getIsReportFetching,
  getReportData,
} from "store/views/reports";
import { withVar } from "themes/helpers";

import { ChartPanel, Filters } from "../components";
import { ChartBreakdown } from "../components/ChartBreakdown";
import { AgentCell, RatingCell, TeamCell } from "../components/PerformanceTableCells";
import { PerformanceTablePromo } from "../components/PerformanceTablePromo";
import { ReportLocalTimeLabel } from "../components/ReportLocaltimeLabel";
import { TopRatedAgentsTable } from "../components/TopRatedAgentsTable";
import { getChartOptions, getExportFilename } from "../helpers";
import { card, cardBody, cardTitle } from "../styled/card";
import { containerFluid } from "../styled/layout";

interface IStateProps {
  isFetching: boolean;
  data?: ITicketRatingsData;
  distribution: ReportDistribution;
  dateRange: IDateRangeWithCompareFilter;
}

type IProps = IStateProps;

const TicketRatings: React.FC<IProps> = ({ isFetching, data, distribution, dateRange }) => {
  useLastVisitedReport(ReportType.TicketRatings);
  const ranking = (data && data.ranking) || [];
  const isTeam = useSelector(selectCurrentSubscriptionPlan) === Plan.Team;
  const teams = useSelector(selectTeams);
  const agents = useSelector(selectAgents);
  const hasPerformanceReport = useSelector(selectFeatureTogglePerformanceReport);
  const ticketRatingChartLegend = useMemo(() => data && data.ticketRating.summary, [data]);
  const ticketRatingChartOptions = useMemo(
    () => data && getChartOptions(data.ticketRating.series, data.ticketRating.labels, distribution, null),
    [data, distribution]
  );
  const ticketRatingPerAgent = data?.ticketRatingPerAgent;
  const ticketRatingPerTeam = data?.ticketRatingPerTeam;
  const totalRating = ticketRatingPerAgent?.find(el => el.id === "total");
  const totalRatingCount = totalRating?.total;
  const ratedNeutralPercentValue = totalRating?.neutral;
  const ratedBadPercentValue = totalRating?.bad;
  const ratedGoodPercentValue = totalRating?.good;

  const teamColumns = useMemo(() => {
    return [
      {
        Header: "Name",
        accessor: "id",
        Cell: ({ value, row }: { value: string; row: RowInfo["row"] }): JSX.Element => (
          <TeamCell value={value} row={row} teamName={teams[value]?.name} totalLabel="Rating" />
        ),
        className: "even",
      },
      {
        Header: (
          <>
            <span css={tableNeutralHeaderStyles}></span>
            <span>Rated okay</span>
          </>
        ),
        accessor: "neutral",
        Cell: ({ value, original }: { value: number; original: ITicketRatingsPerEntity }): JSX.Element => (
          <RatingCell value={value} valueToCompare={ratedNeutralPercentValue} original={original} accessor="neutral" />
        ),
        className: "odd",
      },
      {
        Header: (
          <>
            <span css={tableBadHeaderStyles}></span>
            <span>Rated bad</span>
          </>
        ),
        accessor: "bad",
        Cell: ({ value, original }: { value: number; original: ITicketRatingsPerEntity }): JSX.Element => (
          <RatingCell value={value} valueToCompare={ratedBadPercentValue} original={original} accessor="bad" />
        ),
        className: "even",
      },
      {
        Header: (
          <>
            <span css={tableGoodHeaderStyles}></span>
            <span>Rated great</span>
          </>
        ),
        accessor: "good",
        Cell: ({ value, original }: { value: number; original: ITicketRatingsPerEntity }): JSX.Element => (
          <RatingCell value={value} valueToCompare={ratedGoodPercentValue} original={original} accessor="good" />
        ),
        className: "odd",
      },
    ];
  }, [data, teams, ratedBadPercentValue, ratedGoodPercentValue, ratedNeutralPercentValue, totalRatingCount]);

  const agentColumns = useMemo(() => {
    return [
      {
        Header: "Name",
        accessor: "id",
        Cell: ({ value, row }: { value: string; row: RowInfo["row"] }): JSX.Element => {
          const agent = agents[value];
          const { avatar, name } = agent ?? {};
          return <AgentCell value={value} row={row} agentName={name} avatar={avatar} totalLabel="Rating" />;
        },
        className: "even",
      },
      {
        Header: (
          <>
            <span css={tableNeutralHeaderStyles}></span>
            <span>Rated okay</span>
          </>
        ),
        accessor: "neutral",
        Cell: ({ value, original }: { value: number; original: ITicketRatingsPerEntity }): JSX.Element => (
          <RatingCell value={value} valueToCompare={ratedNeutralPercentValue} original={original} accessor="neutral" />
        ),
        className: "odd",
      },
      {
        Header: (
          <>
            <span css={tableBadHeaderStyles}></span>
            <span>Rated bad</span>
          </>
        ),
        accessor: "bad",
        Cell: ({ value, original }: { value: number; original: ITicketRatingsPerEntity }): JSX.Element => (
          <RatingCell value={value} valueToCompare={ratedBadPercentValue} original={original} accessor="bad" />
        ),
        className: "even",
      },
      {
        Header: (
          <>
            <span css={tableGoodHeaderStyles}></span>
            <span>Rated great</span>
          </>
        ),
        accessor: "good",
        Cell: ({ value, original }: { value: number; original: ITicketRatingsPerEntity }): JSX.Element => (
          <RatingCell value={value} valueToCompare={ratedGoodPercentValue} original={original} accessor="good" />
        ),
        className: "odd",
      },
    ];
  }, [data, teams, ratedBadPercentValue, ratedGoodPercentValue, ratedNeutralPercentValue, totalRatingCount]);

  return (
    <>
      <Filters />
      <div css={containerFluid}>
        <ChartPanel
          title="Ticket satisfaction"
          tooltip="This chart shows you how your customers rate your ticket responses."
          loading={isFetching}
          legend={ticketRatingChartLegend}
          options={ticketRatingChartOptions}
          exportFileName={getExportFilename("ticket_rating", dateRange, distribution)}
          customComponent={
            hasPerformanceReport ? (
              <>
                <ChartBreakdown
                  sort={{ id: "good", desc: true }}
                  customTitle="Ticket satisfaction per team"
                  loading={isFetching}
                  breakdownData={{ columns: teamColumns, data: ticketRatingPerTeam }}
                  truncateData={ticketRatingPerTeam && ticketRatingPerTeam.length > 5}
                />
                <Block marginBottom={withVar(SpacingToken.Spacing7)} />
                <ChartBreakdown
                  sort={{ id: "good", desc: true }}
                  customTitle="Ticket satisfaction per agent"
                  loading={isFetching}
                  breakdownData={{ columns: agentColumns, data: ticketRatingPerAgent }}
                  fallbackComponent={isTeam ? <PerformanceTablePromo /> : undefined}
                  truncateData={ticketRatingPerAgent && ticketRatingPerAgent.length > 5}
                  disableExpand={isTeam}
                />
                <Block marginBottom={withVar(SpacingToken.Spacing7)} />
              </>
            ) : null
          }
        />
        <ReportLocalTimeLabel />
        {!hasPerformanceReport && ranking.length > 0 && (
          <div css={card}>
            <div css={cardTitle}>
              <span>Rated tickets per agent</span>
            </div>
            <div css={cardBody}>
              <TopRatedAgentsTable ranking={ranking} type="tickets" />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const mapStateToProps = (state: IStoreState): IStateProps => ({
  isFetching: getIsReportFetching(state, ReportType.TicketRatings),
  data: getReportData(state, ReportType.TicketRatings),
  distribution: getDistribution(state),
  dateRange: getFilterDateRange(state),
});

export default connect(mapStateToProps, null)(TicketRatings);

const tableHeaderStyles = css`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin-right: ${withVar(SpacingToken.Spacing1)};
  margin-top: ${withVar(SpacingToken.Spacing1)};
`;

const tableNeutralHeaderStyles = css`
  ${tableHeaderStyles};
  background-color: ${withVar(DesignToken.DecorBlue500)};
`;
const tableBadHeaderStyles = css`
  ${tableHeaderStyles};
  background-color: ${withVar(DesignToken.DecorRed500)};
`;

const tableGoodHeaderStyles = css`
  ${tableHeaderStyles};
  background-color: ${withVar(DesignToken.DecorGreen500)};
`;
